import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, MapPin, BarChart3, Crown, Calendar, User, Brain, Database, MessageSquare, Zap, Search } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { trackEvent } from '../utils/posthog';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { supabase } from '../utils/supabaseClient';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  image_url: string | null;
  published_at: string;
}

const LandingPage: React.FC = () => {
  useScrollToTop();
  const [latestPosts, setLatestPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    // Track page view
    trackEvent('page_view', {
      page: 'landing_page'
    });

    // Fetch latest blog posts
    const fetchLatestPosts = async () => {
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('id, title, slug, excerpt, category, image_url, published_at')
          .eq('status', 'published')
          .order('published_at', { ascending: false })
          .limit(2);

        if (error) throw error;
        setLatestPosts(data || []);
      } catch (error) {
        console.error('Error fetching latest blog posts:', error);
      }
    };

    fetchLatestPosts();
  }, []);

  const handleCTAClick = (ctaName: string, location: string) => {
    trackEvent('cta_click', {
      cta_name: ctaName,
      location: location
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#F9F7FE] to-[#EADFFF] py-16 md:py-20 overflow-hidden">
        <div className="absolute right-0 top-0 w-64 h-64 bg-[#FFD166] rounded-full opacity-20 -mr-32 -mt-16"></div>
        <div className="absolute left-0 bottom-0 w-80 h-80 bg-[#7B4AE2] rounded-full opacity-10 -ml-40 -mb-40"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center">
            <div className="w-full lg:w-1/2 mb-12 lg:mb-0">
              <div className="inline-block mb-4 px-3 py-1 bg-[#F0E9FF] text-[#7B4AE2] rounded-full text-sm font-medium">
                For 11th Grade Students
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Find the <span className="text-[#7B4AE2]">Right College</span> for <br />
                You — Instantly.
              </h1>
              <p className="text-gray-600 text-lg mb-8">
                Compare cutoffs, discover your chances, and choose the <br />
                best 11th-grade colleges based on your profile.(Currently covering Mumbai and Pune)
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/how-it-works" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-[#7B4AE2] text-white rounded-lg hover:bg-[#6A3ACF] transition-colors font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  onClick={() => handleCTAClick('learn_how_it_works', 'hero')}
                >
                  Learn How It Works
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link 
                  to="/college-database" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#7B4AE2] border-2 border-[#7B4AE2] rounded-lg hover:bg-[#F0E9FF] transition-colors font-semibold text-lg"
                  onClick={() => handleCTAClick('browse_colleges', 'hero')}
                >
                  Browse Colleges
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <Link to="/disclaimer" className="hover:underline">
                  Disclaimer: 11thadmission.com is an independent educational consulting service not affiliated with any government body or educational institution.
                </Link>
              </div>
            </div>
            
            <div className="w-full lg:w-1/2">
              <div className="bg-white rounded-xl shadow-xl p-6 max-w-md mx-auto transform rotate-1">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold">Your College Matches</h3>
                </div>
                
                <div className="mb-3 p-4 bg-[#F9F9F9] rounded-lg flex justify-between items-center">
                  <div>
                    <h4 className="font-medium">Fergusson College</h4>
                    <p className="text-sm text-gray-500">Science Stream - Pune</p>
                  </div>
                  <div className="bg-[#FFEEB9] text-[#B78103] text-sm font-medium py-1 px-3 rounded-full">
                    85% Match
                  </div>
                </div>

                <div className="mb-3 p-4 bg-[#F9F9F9] rounded-lg flex justify-between items-center">
                  <div>
                    <h4 className="font-medium">Modern College</h4>
                    <p className="text-sm text-gray-500">Science Stream - Pune</p>
                  </div>
                  <div className="bg-[#FFEEB9] text-[#B78103] text-sm font-medium py-1 px-3 rounded-full">
                    80% Match
                  </div>
                </div>

                <div className="mb-3 p-4 bg-[#F9F9F9] rounded-lg flex justify-between items-center">
                  <div>
                    <h4 className="font-medium">Garware College</h4>
                    <p className="text-sm text-gray-500">Science Stream - Pune</p>
                  </div>
                  <div className="bg-[#FFEEB9] text-[#B78103] text-sm font-medium py-1 px-3 rounded-full">
                    75% Match
                  </div>
                </div>
                
                <div className="text-center text-sm text-gray-500 my-2">
                  + 7 more matches
                </div>
                
                <Link 
                  to="/register" 
                  className="block w-full bg-[#18CDCA] text-white rounded-lg py-2 font-medium hover:bg-opacity-90 transition-colors text-center"
                  onClick={() => handleCTAClick('see_all_matches', 'hero_card')}
                >
                  See All Matches
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">How It Works</h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Three simple steps to find your perfect college match
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Step 1 */}
            <div className="text-center">
              <div className="bg-[#F0E9FF] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="bg-[#7B4AE2] text-white w-10 h-10 rounded-full flex items-center justify-center">1</div>
              </div>
              <h3 className="text-lg font-semibold mb-2">Enter Your Details</h3>
              <p className="text-gray-600">
                Share your marks, preferences, stream and location to help us find the best matches.
              </p>
            </div>
            
            {/* Step 2 */}
            <div className="text-center">
              <div className="bg-[#E0F9F9] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="bg-[#18CDCA] text-white w-10 h-10 rounded-full flex items-center justify-center">2</div>
              </div>
              <h3 className="text-lg font-semibold mb-2">Get Predictions</h3>
              <p className="text-gray-600">
                Our AI analyzes historical data to predict cutoffs and admission chances at various colleges.
              </p>
            </div>
            
            {/* Step 3 */}
            <div className="text-center">
              <div className="bg-[#FFF5D9] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="bg-[#FFD166] text-white w-10 h-10 rounded-full flex items-center justify-center">3</div>
              </div>
              <h3 className="text-lg font-semibold mb-2">Choose Confidently</h3>
              <p className="text-gray-600">
                Select your 10 best-fit colleges and apply with certainty of admission.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* College Predictor Highlight Section */}
      <section className="py-20 bg-gradient-to-br from-[#7B4AE2] to-[#5A35A7] text-white relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full -mr-48 -mt-48"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#FFD166] opacity-10 rounded-full -ml-40 -mb-40"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-white bg-opacity-20 text-white rounded-full text-sm font-medium">
              🎯 Premium Feature
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              The Most Advanced <br />
              <span className="text-[#FFD166]">College Predictor</span> in India
            </h2>
            <p className="text-white text-opacity-90 text-lg max-w-3xl mx-auto">
              Our AI-powered College Predictor doesn't just show you colleges—it intelligently matches you with institutions where you have the highest chances of admission, using advanced filtering and predictive analytics.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Features */}
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#FFD166] rounded-lg flex items-center justify-center">
                  <Star className="h-6 w-6 text-[#7B4AE2]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">🎯 Top 10 High-Chance Colleges</h3>
                  <p className="text-white text-opacity-80">
                    Instantly see your best admission prospects with confidence scores and AI-enhanced recommendations.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#18CDCA] rounded-lg flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">🗺️ Smart Location Intelligence</h3>
                  <p className="text-white text-opacity-80">
                    City and area-based filtering with cascading dropdowns. Find colleges in specific areas like Kothrud, Andheri, or Shivajinagar.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#EF476F] rounded-lg flex items-center justify-center">
                  <User className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">👥 Complete Category Support</h3>
                  <p className="text-white text-opacity-80">
                    Filter by reservation categories (OPEN, SC, ST, OBC, NT, EWS, SEBC) and get accurate predictions for your category.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#06D6A0] rounded-lg flex items-center justify-center">
                  <BarChart3 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">📊 Multi-Language & Stream Support</h3>
                  <p className="text-white text-opacity-80">
                    Choose from English, Marathi, Hindi, Urdu mediums across Science, Commerce, Arts, and Vocational streams.
                  </p>
                </div>
              </div>
            </div>

            {/* Right side - Interactive Preview */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-6 transform rotate-2 hover:rotate-0 transition-transform duration-300">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Your Personalized Results</h3>
                  <p className="text-sm text-gray-600">Based on 85% marks in Science stream</p>
                </div>
                
                {/* Filter tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    City: Mumbai
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Stream: Science
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    Medium: English
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Category: OPEN
                  </span>
                </div>

                {/* Top colleges preview */}
                <div className="space-y-3">
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-gray-900">St. Xavier's College</h4>
                        <p className="text-xs text-gray-500">Marine Lines, Mumbai</p>
                      </div>
                      <div className="text-right">
                        <div className="bg-green-100 text-green-800 text-xs font-medium py-1 px-2 rounded-full">
                          High Chance
                        </div>
                        <p className="text-xs text-gray-500 mt-1">95% confidence</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-gray-900">Ruia College</h4>
                        <p className="text-xs text-gray-500">Matunga, Mumbai</p>
                      </div>
                      <div className="text-right">
                        <div className="bg-green-100 text-green-800 text-xs font-medium py-1 px-2 rounded-full">
                          High Chance
                        </div>
                        <p className="text-xs text-gray-500 mt-1">92% confidence</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-gray-900">K.C. College</h4>
                        <p className="text-xs text-gray-500">Churchgate, Mumbai</p>
                      </div>
                      <div className="text-right">
                        <div className="bg-yellow-100 text-yellow-800 text-xs font-medium py-1 px-2 rounded-full">
                          Medium Chance
                        </div>
                        <p className="text-xs text-gray-500 mt-1">78% confidence</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">+ 47 more colleges analyzed</span>
                    <span className="text-[#7B4AE2] font-medium">View All Results →</span>
                  </div>
                </div>
              </div>

              {/* Floating stats */}
              <div className="absolute -top-4 -left-4 bg-[#FFD166] text-[#7B4AE2] rounded-lg p-3 shadow-lg">
                <div className="text-center">
                  <div className="text-lg font-bold">1000+</div>
                  <div className="text-xs">Colleges</div>
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-[#18CDCA] text-white rounded-lg p-3 shadow-lg">
                <div className="text-center">
                  <div className="text-lg font-bold">95%</div>
                  <div className="text-xs">Accuracy</div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            <Link 
              to="/register" 
              className="inline-flex items-center px-8 py-4 bg-[#FFD166] text-[#4A2C7A] rounded-lg hover:bg-opacity-90 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              onClick={() => handleCTAClick('try_college_predictor', 'predictor_highlight')}
            >
              Try College Predictor
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <p className="text-white text-opacity-70 text-sm mt-3">
              No credit card required • Get instant results • Premium features available
            </p>
          </div>
        </div>
      </section>

      {/* AI Chatbot Highlight Section */}
      <section className="py-20 bg-gradient-to-br from-[#18CDCA] to-[#0FA19E] text-white relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-white opacity-5 rounded-full -ml-48 -mt-48"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-[#7B4AE2] opacity-10 rounded-full -mr-40 -mb-40"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-white bg-opacity-20 text-white rounded-full text-sm font-medium">
              🤖 AI-Powered Assistant
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Meet Your Personal <br />
              <span className="text-[#FFD166]">AI College Advisor</span>
            </h2>
            <p className="text-white text-opacity-90 text-lg max-w-3xl mx-auto">
              Our advanced AI chatbot uses semantic search, embeddings, and multi-modal intelligence to provide instant, accurate answers to all your college admission questions.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Chat Interface Preview */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden transform -rotate-2 hover:rotate-0 transition-transform duration-300">
                {/* Chat Header */}
                <div className="bg-gradient-to-r from-[#7B4AE2] to-[#5A35A7] p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">AI</span>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">College Admission Assistant</h3>
                      <p className="text-white text-opacity-80 text-sm">Online • Powered by Advanced AI</p>
                    </div>
                  </div>
                </div>

                {/* Chat Messages */}
                <div className="p-4 space-y-4 h-80 overflow-y-auto bg-gray-50">
                  {/* User message */}
                  <div className="flex justify-end">
                    <div className="bg-[#7B4AE2] text-white rounded-lg px-4 py-2 max-w-xs">
                      <p className="text-sm">I got 85% in 10th. Which Science colleges in Mumbai can I get?</p>
                    </div>
                  </div>

                  {/* AI response */}
                  <div className="flex justify-start">
                    <div className="bg-white border rounded-lg px-4 py-3 max-w-sm shadow-sm">
                      <p className="text-sm text-gray-800 mb-2">
                        🎯 Based on your 85% marks, here are top Science colleges in Mumbai:
                      </p>
                      <div className="space-y-2">
                        <div className="bg-green-50 p-2 rounded border-l-4 border-green-400">
                          <p className="text-xs font-medium text-green-800">St. Xavier's College</p>
                          <p className="text-xs text-green-600">High chance • Marine Lines</p>
                        </div>
                        <div className="bg-green-50 p-2 rounded border-l-4 border-green-400">
                          <p className="text-xs font-medium text-green-800">Ruia College</p>
                          <p className="text-xs text-green-600">High chance • Matunga</p>
                        </div>
                        <div className="bg-yellow-50 p-2 rounded border-l-4 border-yellow-400">
                          <p className="text-xs font-medium text-yellow-800">K.C. College</p>
                          <p className="text-xs text-yellow-600">Medium chance • Churchgate</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">+ 12 more colleges analyzed</p>
                    </div>
                  </div>

                  {/* User follow-up */}
                  <div className="flex justify-end">
                    <div className="bg-[#7B4AE2] text-white rounded-lg px-4 py-2 max-w-xs">
                      <p className="text-sm">Tell me about Fergusson College cutoffs</p>
                    </div>
                  </div>

                  {/* AI detailed response */}
                  <div className="flex justify-start">
                    <div className="bg-white border rounded-lg px-4 py-3 max-w-sm shadow-sm">
                      <p className="text-sm text-gray-800 mb-2">
                        📊 Fergusson College (Pune) - Science Stream:
                      </p>
                      <div className="text-xs space-y-1">
                        <p><span className="font-medium">2024 Cutoff:</span> 92.5% (Round 1)</p>
                        <p><span className="font-medium">2023 Cutoff:</span> 91.8% (Round 1)</p>
                        <p><span className="font-medium">Trend:</span> ↗️ Rising by 0.7%</p>
                        <p><span className="font-medium">Your Chance:</span> <span className="text-red-600">Low (need 7.5% more)</span></p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Chat Input */}
                <div className="p-4 border-t bg-white">
                  <div className="flex items-center space-x-2">
                    <input 
                      type="text" 
                      placeholder="Ask about colleges, cutoffs, admission process..."
                      className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#18CDCA]"
                      disabled
                    />
                    <button className="bg-[#18CDCA] text-white rounded-lg px-4 py-2 text-sm font-medium">
                      Send
                    </button>
                  </div>
                </div>
              </div>

              {/* Floating feature badges */}
              <div className="absolute -top-4 -right-4 bg-[#FFD166] text-[#7B4AE2] rounded-lg p-3 shadow-lg">
                <div className="text-center">
                  <div className="text-lg font-bold">1000+</div>
                  <div className="text-xs">Embeddings</div>
                </div>
              </div>

              <div className="absolute -bottom-4 -left-4 bg-[#7B4AE2] text-white rounded-lg p-3 shadow-lg">
                <div className="text-center">
                  <div className="text-lg font-bold">&lt;1s</div>
                  <div className="text-xs">Response</div>
                </div>
              </div>
            </div>

            {/* Right side - Features */}
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#FFD166] rounded-lg flex items-center justify-center">
                  <Star className="h-6 w-6 text-[#18CDCA]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">🧠 Advanced Semantic Search</h3>
                  <p className="text-white text-opacity-80">
                    1000+ college embeddings cached with sentence transformer model (all-MiniLM-L6-v2) for intelligent discovery using cosine similarity matching.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#7B4AE2] rounded-lg flex items-center justify-center">
                  <BarChart3 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">🔗 Live Database Integration</h3>
                  <p className="text-white text-opacity-80">
                    Supabase connected with real-time cutoff analysis, stream filtering, and location-based searches with instant data updates.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#EF476F] rounded-lg flex items-center justify-center">
                  <Crown className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">🤖 Multi-Modal AI System</h3>
                  <p className="text-white text-opacity-80">
                    Primary semantic search → Traditional database filtering → OpenAI + web search fallback with intelligent query routing.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#06D6A0] rounded-lg flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">🎯 Query Processing Excellence</h3>
                  <p className="text-white text-opacity-80">
                    Handles cutoff analysis, location searches, college-specific queries, and general stream-specific guidance with context-aware responses.
                  </p>
                </div>
              </div>

              {/* Performance Metrics */}
              <div className="bg-white bg-opacity-10 rounded-lg p-6 mt-8">
                <h4 className="text-lg font-semibold mb-4">🚀 Performance Metrics</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#FFD166]">2MB</div>
                    <div className="text-sm text-white text-opacity-80">Embeddings Cache</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#FFD166]">95%</div>
                    <div className="text-sm text-white text-opacity-80">Accuracy Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#FFD166]">1000+</div>
                    <div className="text-sm text-white text-opacity-80">Colleges Covered</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#FFD166]">24/7</div>
                    <div className="text-sm text-white text-opacity-80">Availability</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            <Link 
              to="/register" 
              className="inline-flex items-center px-8 py-4 bg-[#FFD166] text-[#0F7B7A] rounded-lg hover:bg-opacity-90 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              onClick={() => handleCTAClick('try_ai_chatbot', 'chatbot_highlight')}
            >
              Try AI Chatbot
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <p className="text-white text-opacity-70 text-sm mt-3">
              Instant answers • Semantic search • Multi-modal intelligence
            </p>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-16 bg-[#F9F7FE]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Why Choose Our College Finder</h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Powerful tools to make your college search easier and more accurate
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Feature 1 */}
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#F0E9FF] rounded-lg flex items-center justify-center mb-4">
                <Star className="h-6 w-6 text-[#7B4AE2]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">AI-Powered Predictions</h3>
              <p className="text-gray-600">
                Our smart algorithms analyze your profile and predict your chances of admission at each college.
              </p>
            </div>
            
            {/* Feature 2 */}
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#FFE9ED] rounded-lg flex items-center justify-center mb-4">
                <MapPin className="h-6 w-6 text-[#EF476F]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">City-Wise Cutoffs</h3>
              <p className="text-gray-600">
                Explore colleges in Pune, Mumbai, and other major cities with accurate cutoff predictions.
              </p>
            </div>
            
            {/* Feature 3 */}
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#E0F9F9] rounded-lg flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-[#18CDCA]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Personalized Recommendations</h3>
              <p className="text-gray-600">
                Filter colleges by stream, category, location, and more to find your perfect match.
              </p>
            </div>
            
            {/* Feature 4 */}
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#FFF5D9] rounded-lg flex items-center justify-center mb-4">
                <Crown className="h-6 w-6 text-[#FFD166]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Freemium Model</h3>
              <p className="text-gray-600">
                Get basic insights for free forever, with the option to upgrade for deeper analysis.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      
      
      {/* Latest Blog Posts Section */}
      {latestPosts.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Recent Updates</h2>
              <p className="text-gray-600 max-w-xl mx-auto">
                Stay informed with our latest articles and insights
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {latestPosts.map(post => (
                <Link 
                  key={post.id} 
                  to={`/blog/${post.slug}`}
                  className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                  onClick={() => trackEvent('blog_post_click', { post_id: post.id, post_title: post.title })}
                >
                  {post.image_url && (
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={post.image_url} 
                        alt={post.title} 
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F0E9FF] text-[#7B4AE2]">
                        {post.category}
                      </span>
                      <span className="mx-2">•</span>
                      <span className="flex items-center">
                        <Calendar className="h-3.5 w-3.5 mr-1" />
                        {new Date(post.published_at).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900 hover:text-[#7B4AE2]">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center text-[#7B4AE2] font-medium">
                      Read more
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <Link 
                to="/blog" 
                className="inline-flex items-center px-4 py-2 border border-[#7B4AE2] text-[#7B4AE2] rounded-lg hover:bg-[#F0E9FF] transition-colors"
                onClick={() => handleCTAClick('view_all_posts', 'blog_section')}
              >
                View All Posts
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#7B4AE2] to-[#5A35A7] text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between max-w-4xl mx-auto">
            <div className="mb-8 md:mb-0">
              <h2 className="text-3xl font-bold mb-4">Ready to Find Your Perfect College?</h2>
              <p className="text-white text-opacity-90">
                Get started today and receive your personalized college recommendations.
              </p>
            </div>
            <Link 
              to="/register" 
              className="btn bg-white text-[#7B4AE2] hover:bg-opacity-90 transition-colors py-3 px-6 text-base font-semibold rounded-lg"
              onClick={() => handleCTAClick('get_started_free', 'footer_cta')}
            >
              Get Started — It's Free
            </Link>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default LandingPage;